import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSeo } from '../utils/useSeo';

type DocKey = 'privacy' | 'kvkk';

const CONTACT_EMAIL = 'klinikvet.app@gmail.com';
const LAST_UPDATED = '1 Ekim 2026';

interface Section {
  title: string;
  body: (string | string[])[];
}

interface LegalDoc {
  title: string;
  intro: string;
  sections: Section[];
}

const DOCS: Record<DocKey, LegalDoc> = {
  privacy: {
    title: 'Gizlilik Politikası',
    intro:
      'Bu politika, KlinikVet web paneli ve KlinikVet mobil uygulaması hizmetlerinde kişisel verilerin nasıl işlendiğini açıklar. MSE Soft tarafından geliştirilen bu hizmetler, veteriner kliniklerinin hasta kayıtlarını yönetmesini ve hasta sahiplerinin kendi hayvanlarının bilgilerine ulaşmasını sağlar.',
    sections: [
      {
        title: '1. Rollerimiz',
        body: [
          'Hasta sahibi ve hayvan sağlık kayıtları için veri sorumlusu, kaydı oluşturan veteriner kliniğidir. MSE Soft, bu verileri klinik adına ve klinikin talimatlarıyla işleyen veri işleyendir.',
          'Klinik hesabı bilgileri ve demo/iletişim taleplerinde ise veri sorumlusu MSE Soft’tur.',
        ],
      },
      {
        title: '2. İşlediğimiz veriler',
        body: [
          [
            'Hasta sahibi: ad soyad, telefon, e-posta, adres, (varsa) çip ve pasaport numarası.',
            'Hayvan: ad, tür, ırk, yaş, kilo, renk, cinsiyet, kısırlaştırma durumu, protokol numarası.',
            'Sağlık kayıtları: randevular, muayene (şikâyet, teşhis, tedavi, reçete, notlar), aşı ve ilaç kayıtları.',
            'Hesap: giriş e-posta adresi ve kimlik doğrulama bilgileri.',
            'Demo/iletişim formu: formda yazdığınız ad, e-posta, telefon ve mesaj.',
          ],
        ],
      },
      {
        title: '3. Kullanım amaçları',
        body: [
          'Verileri yalnızca klinik hizmetlerini sunmak için kullanırız: kayıtların tutulması, randevu ve aşı takibi, hasta sahibine kendi hayvanının bilgilerinin gösterilmesi, hesap güvenliği ve gelen taleplere yanıt verilmesi.',
        ],
      },
      {
        title: '4. Paylaşım ve hizmet sağlayıcılar',
        body: [
          'Verilerinizi satmaz, reklam amacıyla kullanmaz ve üçüncü taraf reklam veya izleme (analitik) araçları kullanmayız.',
          [
            'Supabase: veritabanı ve kimlik doğrulama. Veriler Avrupa Birliği’nde (Frankfurt) bulunan sunucularda tutulur.',
            'Netlify: web sitesinin barındırılması.',
            'E-posta altyapısı: demo/iletişim taleplerinin tarafımıza iletilmesi.',
          ],
          'Yasal bir yükümlülük gerektirmedikçe verileriniz başka kişi veya kurumlarla paylaşılmaz.',
        ],
      },
      {
        title: '5. Cihazda saklanan bilgiler',
        body: [
          'Oturumunuzu açık tutmak için tarayıcınızın/cihazınızın yerel depolamasında oturum bilgisi ve uygulamanın hızlı çalışması için önbellek verisi saklanır. Reklam veya izleme amaçlı çerez kullanmayız.',
        ],
      },
      {
        title: '6. Güvenlik',
        body: [
          'Bağlantılar HTTPS ile şifrelenir. Veritabanında satır düzeyi erişim denetimi uygulanır; bir klinik yalnızca kendi kayıtlarına, hasta sahibi yalnızca kendisine bağlı kayıtlara erişebilir.',
        ],
      },
      {
        title: '7. Saklama süresi',
        body: [
          'Veriler, klinikle hizmet ilişkisi sürdüğü müddetçe saklanır. Silme talepleri aşağıdaki hakları bölümünde anlatılan yolla iletilebilir.',
          'Mobil uygulamada Profil bölümündeki "Hesabı Sil" ile giriş hesabınız ve cihaz bildirim kaydınız hemen silinir; bu e-postayla uygulamaya tekrar giriş yapılamaz. Hayvanlarınıza ait klinik kayıtları, kliniğin yasal saklama yükümlülükleri nedeniyle klinikte kalır; silinmesini kliniğe talep edebilirsiniz.',
        ],
      },
      {
        title: '8. Haklarınız',
        body: [
          'Verilerinize erişme, düzeltme, silme, işlenmesine itiraz etme ve verilerinizin aktarıldığı kişileri öğrenme hakkına sahipsiniz. Hasta sahibi iseniz talebinizi öncelikle kaydınızı tutan kliniğe iletebilirsiniz; bize de aşağıdaki adresten yazabilirsiniz.',
        ],
      },
      {
        title: '9. Değişiklikler',
        body: [
          'Bu politika güncellenebilir. Güncel sürüm her zaman bu sayfada yayımlanır.',
        ],
      },
    ],
  },
  kvkk: {
    title: 'KVKK Aydınlatma Metni',
    intro:
      '6698 sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) m.10 uyarınca, kişisel verilerinizin işlenmesine ilişkin sizi bilgilendiririz.',
    sections: [
      {
        title: '1. Veri sorumlusu',
        body: [
          'Hasta sahibi ve hayvan kayıtlarında veri sorumlusu, kaydı oluşturan veteriner kliniğidir; MSE Soft veri işleyen olarak hareket eder. Klinik hesabı ve demo/iletişim talepleri bakımından veri sorumlusu MSE Soft’tur.',
        ],
      },
      {
        title: '2. İşlenen kişisel veriler ve amaçlar',
        body: [
          'Kimlik ve iletişim verileri (ad soyad, telefon, e-posta, adres, çip/pasaport numarası) ile hayvanınıza ait randevu, muayene, aşı ve ilaç kayıtları; veteriner hizmetinin yürütülmesi, kayıtların tutulması, randevu ve aşı takibi, hesap güvenliğinin sağlanması ve taleplerinize yanıt verilmesi amacıyla işlenir.',
        ],
      },
      {
        title: '3. Hukuki sebepler',
        body: [
          'Verileriniz KVKK m.5/2 kapsamında; bir sözleşmenin kurulması veya ifasıyla doğrudan ilgili olması, hukuki yükümlülüğün yerine getirilmesi ve veri sorumlusunun meşru menfaati hukuki sebeplerine dayanılarak işlenir. Bu sebeplerin kapsamı dışında kalan işlemeler için açık rızanız alınır.',
        ],
      },
      {
        title: '4. Verilerin aktarılması',
        body: [
          'Verileriniz; barındırma ve veritabanı hizmeti aldığımız Supabase ve Netlify ile, yalnızca hizmetin sunulması amacıyla paylaşılır. Veritabanı sunucuları Avrupa Birliği’nde (Frankfurt) bulunduğundan bu paylaşım KVKK m.9 kapsamında yurt dışına aktarım niteliğindedir ve ilgili mevzuata uygun şekilde gerçekleştirilir.',
        ],
      },
      {
        title: '5. Toplama yöntemi',
        body: [
          'Verileriniz, kliniğin panele girdiği kayıtlar, mobil uygulama ve iletişim/demo formu üzerinden elektronik ortamda toplanır.',
        ],
      },
      {
        title: '6. KVKK m.11 kapsamındaki haklarınız',
        body: [
          [
            'Kişisel verilerinizin işlenip işlenmediğini öğrenme,',
            'İşlenmişse buna ilişkin bilgi talep etme,',
            'İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,',
            'Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme,',
            'Eksik veya yanlış işlenmişse düzeltilmesini isteme,',
            'Kanunun 7. maddesi çerçevesinde silinmesini veya yok edilmesini isteme,',
            'Bu işlemlerin aktarıldığı üçüncü kişilere bildirilmesini isteme,',
            'Otomatik sistemlerle analiz edilmesi sonucu aleyhinize bir sonuç çıkmasına itiraz etme,',
            'Kanuna aykırı işleme nedeniyle zarara uğramanız hâlinde zararın giderilmesini talep etme.',
          ],
        ],
      },
      {
        title: '7. Başvuru',
        body: [
          'Haklarınıza ilişkin taleplerinizi kaydınızı tutan kliniğe veya aşağıdaki e-posta adresine iletebilirsiniz. Başvurular en geç otuz gün içinde sonuçlandırılır.',
        ],
      },
    ],
  },
};

const LegalPage: React.FC<{ doc: DocKey }> = ({ doc }) => {
  const content = DOCS[doc];

  useSeo({
    title: `${content.title} | KlinikVet`,
    description: doc === 'kvkk'
      ? 'KlinikVet KVKK aydınlatma metni: kişisel verilerin işlenmesi, saklanması ve haklarınız.'
      : 'KlinikVet gizlilik politikası: hangi verileri işlediğimiz, nasıl koruduğumuz, hesap silme ve haklarınız.',
    path: doc === 'kvkk' ? '/kvkk' : '/gizlilik',
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [content.title]);

  return (
    <div className="min-h-screen bg-[#F7F4ED] text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-slate-900">
            <span className="w-7 h-7 rounded-lg bg-slate-900 flex items-center justify-center">
              <img src="/brand-symbol.png" alt="" className="h-4 w-auto" />
            </span>
            KlinikVet
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Ana sayfa
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{content.title}</h1>
        <p className="mt-2 text-sm text-slate-500">Son güncelleme: {LAST_UPDATED}</p>
        <p className="mt-6 text-[15px] leading-relaxed text-slate-700">{content.intro}</p>

        {content.sections.map(section => (
          <section key={section.title} className="mt-8">
            <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
            <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-slate-700">
              {section.body.map((block, i) =>
                Array.isArray(block) ? (
                  <ul key={i} className="list-disc pl-5 space-y-1.5">
                    {block.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={i}>{block}</p>
                )
              )}
            </div>
          </section>
        ))}

        <section className="mt-10 rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="text-lg font-semibold text-slate-900">İletişim</h2>
          <p className="mt-1 text-[15px] text-slate-700">
            MSE Soft ·{' '}
            <a className="text-teal-700 underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>
        </section>

        <nav className="mt-8 flex gap-4 text-sm">
          <Link to="/gizlilik" className="text-teal-700 hover:underline">Gizlilik Politikası</Link>
          <Link to="/kvkk" className="text-teal-700 hover:underline">KVKK Aydınlatma Metni</Link>
        </nav>
      </main>
    </div>
  );
};

export default LegalPage;
